import type {
  LabBiomarkerHistory,
  LabBiomarkerHistoryResult,
} from "@/types/lab";

export type BiomarkerStatus = "normal" | "out" | "critical" | "unknown";

export interface BiomarkerRange {
  min?: number;
  max?: number;
}

/** The range the bar draws: the newest result that came with one. */
export function currentRange(
  history: LabBiomarkerHistory,
): LabBiomarkerHistoryResult | undefined {
  return history.results.find(
    (r) => r.minRangeValue != null || r.maxRangeValue != null,
  );
}

/** A result against its own lab's range; the lab's interpretation wins when it sent one. */
export function resultStatus(
  result: LabBiomarkerHistoryResult,
): BiomarkerStatus {
  if (result.clinicalInterpretation === "critical") return "critical";
  if (result.clinicalInterpretation === "abnormal") return "out";
  const value = result.valueNumeric;
  const { minRangeValue: min, maxRangeValue: max } = result;
  if (value == null || (min == null && max == null)) {
    return result.clinicalInterpretation === "normal" ? "normal" : "unknown";
  }
  if ((min != null && value < min) || (max != null && value > max)) {
    return "out";
  }
  return "normal";
}

/**
 * The axis for one marker's bar: the reference band sits in the middle with
 * half its width of room either side, stretched to fit every result.
 */
export function rangeDomain(
  values: number[],
  range: BiomarkerRange,
): [number, number] {
  const { min, max } = range;
  let low: number;
  let high: number;
  if (min != null && max != null) {
    const pad = (max - min) / 2 || Math.abs(max) / 2 || 1;
    low = min - pad;
    high = max + pad;
  } else if (max != null) {
    low = Math.min(0, max);
    high = max * 1.5 || 1;
  } else if (min != null) {
    low = min / 2;
    high = min * 1.5 || 1;
  } else {
    low = Math.min(...values);
    high = Math.max(...values);
  }
  low = Math.min(low, ...values);
  high = Math.max(high, ...values);
  const margin = (high - low) * 0.05 || Math.abs(high) * 0.1 || 1;
  return [low - margin, high + margin];
}

export function positionPercent(
  value: number,
  [low, high]: [number, number],
): number {
  return Math.min(100, Math.max(0, ((value - low) / (high - low)) * 100));
}

const DAY_MS = 86_400_000;
const FADE_HALF_LIFE_DAYS = 180;

/**
 * How faint an older result's dot is, by how long before the newest result it
 * was drawn: half as visible every six months, so a gap of years reads as one.
 */
export function ageOpacity(collectedAt: string, newestAt: string): number {
  const days = Math.max(
    0,
    (Date.parse(newestAt) - Date.parse(collectedAt)) / DAY_MS,
  );
  return Math.max(0.15, 0.7 * 0.5 ** (days / FADE_HALF_LIFE_DAYS));
}

/** Markers whose latest result is out of range — the count on the Biomarkers button. */
export function countOutOfRange(histories: LabBiomarkerHistory[]): number {
  return histories.filter((h) => {
    const status = resultStatus(h.results[0]);
    return status === "out" || status === "critical";
  }).length;
}

export function formatRange(
  result: LabBiomarkerHistoryResult | undefined,
): string | null {
  if (!result) return null;
  const { minRangeValue: min, maxRangeValue: max } = result;
  if (min != null && max != null) return `${min}–${max}`;
  if (max != null) return `< ${max}`;
  if (min != null) return `> ${min}`;
  return result.referenceRangeText ?? null;
}

const STATUS_RANK: Record<BiomarkerStatus, number> = {
  critical: 0,
  out: 1,
  normal: 2,
  unknown: 2,
};

/** Markers whose latest result is out of range first, then by name. */
export function sortBiomarkers(
  histories: LabBiomarkerHistory[],
): LabBiomarkerHistory[] {
  return [...histories].sort(
    (a, b) =>
      STATUS_RANK[resultStatus(a.results[0])] -
        STATUS_RANK[resultStatus(b.results[0])] || a.name.localeCompare(b.name),
  );
}
